import { useState } from "react";
import { useAuth } from "../../lib/authContext";
import { Alert, AlertDescription } from "../ui/alert";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { tracker } from "../../lib/tracker";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const { signInWithGoogle, signInWithEmail } = useAuth();

  const handleGoogleLogin = async () => {
    setError(null);
    setGoogleLoading(true);

    try {
      await signInWithGoogle();
      tracker.trackEvent("login_success", { method: "google" });
      // Note: The actual navigation will happen after the OAuth redirect
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to sign in";
      tracker.trackEvent("login_error", {
        error: errorMessage,
        method: "google",
      });
      setError(errorMessage);
      setGoogleLoading(false);
    }
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setEmailLoading(true);

    try {
      await signInWithEmail(email, password);
      tracker.trackEvent("login_success", { method: "email" });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to sign in";
      tracker.trackEvent("login_error", {
        error: errorMessage,
        method: "email",
      });
      setError(errorMessage);
    } finally {
      setEmailLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
      <Card className="w-[380px]">
        <CardHeader>
          <CardTitle className="text-center">Login</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Google Login */}
          <Button
            onClick={() => handleGoogleLogin()}
            className="w-full flex items-center justify-center gap-2"
            disabled={googleLoading || emailLoading}
          >
            <svg
              className="w-5 h-5"
              aria-hidden="true"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z" />
            </svg>
            {googleLoading ? "Signing in..." : "Sign in with Google"}
          </Button>

          {/* Divider */}
          <div className="relative flex items-center">
            <div className="flex-grow border-t border-gray-300 dark:border-gray-600" />
            <span className="mx-3 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
              OR
            </span>
            <div className="flex-grow border-t border-gray-300 dark:border-gray-600" />
          </div>

          {/* Email / Password Login */}
          <form onSubmit={handleEmailLogin} className="space-y-3">
            <Input
              type="email"
              placeholder="Email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              disabled={googleLoading || emailLoading}
            />
            <Input
              type="password"
              placeholder="Password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              disabled={googleLoading || emailLoading}
            />
            <Button
              type="submit"
              variant="outline"
              className="w-full"
              disabled={googleLoading || emailLoading}
            >
              {emailLoading ? "Signing in..." : "Sign in"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
